import is from '@sindresorhus/is';
import i18next from 'i18next';
import type { FunctionComponent } from 'react';
import { useEffect } from 'react';

import type { CompanySettings } from '~/shared/data-contract/company-settings';
import type { LanguageKey } from '~/shared/internationalization';
import { availableI18nLanguages } from '~/shared/internationalization';

import { useCompanySettings } from '../../../api/use-company-settings';

const MIN_TRANSLATION_LENGTH = 10;

const TERM_KEY_MAP = {
    jobClusters: 'jobCluster',
    jobFamilies: 'jobFamily',
    jobLevels: 'jobLevel',
    jobRoles: 'jobRole',
} as const;

type Terminology = NonNullable<CompanySettings['jobArchitectureTerminology']>;

const buildOverwrites = (
    terminology: Terminology,
    languageCode: string,
): Record<string, { readonly many: string; readonly one: string }> => {
    const overwrites: Record<string, { readonly many: string; readonly one: string }> = {};

    for (const [pluralKey, key] of Object.entries(TERM_KEY_MAP)) {
        const terms = terminology[pluralKey as keyof typeof TERM_KEY_MAP];
        const translation = terms?.find((t) => t.language === languageCode);

        if (!is.undefined(translation) && (is.nonEmptyString(translation.one) || is.nonEmptyString(translation.many))) {
            overwrites[key] = { many: translation.many, one: translation.one };
        }
    }

    return overwrites;
};

// In order to know if we have translations loaded we verify that more than MIN_TRANSLATION_LENGTH keys are present
// in the root path of the translation bundle for the current language this means we have more than our
// terminology keys present.
// It's a workaround to prevent i18n not loading langagues w/o requiring a refresh.
const hasEnoughTranslationContent = (existingBundle: Record<string, string>): boolean => {
    return Object.keys(existingBundle).length >= MIN_TRANSLATION_LENGTH;
};

const applyOverwritesForLanguage = (terminology: Terminology, langKey: string, langCode: string): void => {
    const existingBundle = i18next.getResourceBundle(langKey, 'translation');

    if (is.falsy(existingBundle) || !hasEnoughTranslationContent(existingBundle)) {
        return;
    }

    const overwrites = buildOverwrites(terminology, langCode);

    if (Object.keys(overwrites).length > 0) {
        i18next.addResourceBundle(langKey as LanguageKey, 'translation', { overwrites }, true, true);
    }
};

export const TerminologyInitializer: FunctionComponent = () => {
    const { data: companySettings } = useCompanySettings();
    const terminology = companySettings?.jobArchitectureTerminology;

    useEffect(() => {
        if (is.undefined(terminology)) {
            return;
        }

        const currentLang = i18next.language;
        const currentConfig = availableI18nLanguages[currentLang as LanguageKey];

        if (currentConfig !== undefined) {
            applyOverwritesForLanguage(terminology, currentLang, currentConfig.code);
        }

        const handleLanguageChanged = (lng: string): void => {
            const config = availableI18nLanguages[lng as LanguageKey];

            if (config !== undefined) {
                setTimeout(() => {
                    if (terminology !== undefined) {
                        applyOverwritesForLanguage(terminology, lng, config.code);
                    }
                }, 0);
            }
        };

        i18next.on('languageChanged', handleLanguageChanged);

        return () => {
            i18next.off('languageChanged', handleLanguageChanged);
        };
    }, [terminology]);

    return null;
};
